import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { useConfirm } from '../../components/shared/Confirm';
import { useAuth } from '../../context/AuthContext';

const fmtCurrency = n => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const PAYMENT_METHODS    = ['Efectivo', 'Transferencia', 'QR', 'Cupón', 'Otro'];
const EXPENSE_CATEGORIES = ['Operativos', 'Nómina', 'Insumos', 'Arriendo', 'Servicios Públicos', 'Internet', 'Mantenimiento', 'Publicidad', 'Otros'];

const today = new Date().toISOString().split('T')[0];
const INCOME_EMPTY    = { concept: '', amount: '', payment_method: 'Efectivo', income_date: today, branch_id: '' };
const EXPENSE_EMPTY   = { amount: '', category: 'Operativos', subcategory: '', expense_date: today, branch_id: '' };
const RECURRING_EMPTY = { categoria: 'Operativos', monto: '', dia_mes: 1, activo: true, branch_id: '' };

export default function FinancePage() {
  const { hasPlan, can, user } = useAuth();

  // rol 'admin' queda forzado a su propia sucursal
  const isAdminRole  = user?.role === 'admin';
  const userBranchId = user?.branch_id ? String(user.branch_id) : '';

  // ── Estado ─────────────────────────────────────────────────────────────────
  const [branches, setBranches]             = useState([]);
  const [filterBranch, setFilterBranch]     = useState(userBranchId);
  const [summary, setSummary]               = useState(null);
  const [income, setIncome]                 = useState([]);
  const [expenses, setExpenses]             = useState([]);
  const [recurring, setRecurring]           = useState([]);
  const [tab, setTab]                       = useState('summary');
  const [from, setFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [to, setTo]     = useState(today);
  const [loading, setLoading]               = useState(false);
  const [incomeModal, setIncomeModal]       = useState(false);
  const [expenseModal, setExpenseModal]     = useState(false);
  const [recurringModal, setRecurringModal] = useState(null);
  const [incomeForm, setIncomeForm]         = useState(INCOME_EMPTY);
  const [expenseForm, setExpenseForm]       = useState(EXPENSE_EMPTY);
  const [recurringForm, setRecurringForm]   = useState(RECURRING_EMPTY);
  const [saving, setSaving]                 = useState(false);
  const [applyMsg, setApplyMsg]             = useState('');
  // Antes del guard por plan: los hooks no pueden quedar detras de un return.
  const confirmar = useConfirm();

  // ── Callbacks ──────────────────────────────────────────────────────────────
  const loadRecurring = useCallback(() => {
    const params = {};
    if (filterBranch) params.branch_id = filterBranch;
    api.get('/finance/recurring', { params }).then(r => setRecurring(r.data)).catch(() => {});
  }, [filterBranch]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { from, to };
    if (filterBranch) params.branch_id = filterBranch;
    Promise.all([
      api.get('/finance/summary',  { params }),
      api.get('/finance/income',   { params }),
      api.get('/finance/expenses', { params }),
    ]).then(([s, i, e]) => { setSummary(s.data); setIncome(i.data); setExpenses(e.data); })
      .finally(() => setLoading(false));
  }, [from, to, filterBranch]);

  // ── Efectos ────────────────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/branches').then(r => setBranches(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadRecurring();
    api.post('/finance/recurring/apply').catch(() => {});
  }, [loadRecurring]);

  useEffect(() => { load(); }, [load]);

  // ── Guard plan y rol ────────────────────────────────────────────────────────
  if (!hasPlan('has_finance') || !can('super_admin', 'admin_general', 'admin')) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">💰</div>
        <h3>Módulo no disponible</h3>
        <p>El módulo de finanzas está disponible para <strong>Administradores</strong> con plan Premium.</p>
      </div>
    );
  }

  // ── Acciones ───────────────────────────────────────────────────────────────
  const saveIncome = async () => {
    setSaving(true);
    try {
      // backend ignora branch_id para rol 'admin', pero lo enviamos igual
      await api.post('/finance/income', { ...incomeForm, branch_id: incomeForm.branch_id || userBranchId || null });
      setIncomeModal(false); setIncomeForm({ ...INCOME_EMPTY, branch_id: userBranchId }); load();
    } catch { } finally { setSaving(false); }
  };

  const saveExpense = async () => {
    setSaving(true);
    try {
      await api.post('/finance/expenses', { ...expenseForm, branch_id: expenseForm.branch_id || userBranchId || null });
      setExpenseModal(false); setExpenseForm({ ...EXPENSE_EMPTY, branch_id: userBranchId }); load();
    } catch { } finally { setSaving(false); }
  };

  const saveRecurring = async () => {
    setSaving(true);
    try {
      const body = { ...recurringForm, branch_id: recurringForm.branch_id || userBranchId || null };
      if (recurringModal === 'new') {
        await api.post('/finance/recurring', body);
      } else {
        await api.put(`/finance/recurring/${recurringModal.id}`, body);
      }
      setRecurringModal(null);
      setRecurringForm({ ...RECURRING_EMPTY, branch_id: userBranchId });
      loadRecurring();
    } catch { } finally { setSaving(false); }
  };

  const deleteRecurring = async (id) => {
    if (!await confirmar({
      titulo: 'Eliminar gasto fijo',
      mensaje: '¿Eliminar este gasto fijo?',
      detalle: 'Los gastos ya registrados por él se conservan.',
      aceptar: 'Eliminar',
      peligro: true,
    })) return;
    try { await api.delete(`/finance/recurring/${id}`); loadRecurring(); } catch { }
  };

  const applyNow = async () => {
    setSaving(true);
    try {
      const r = await api.post('/finance/recurring/apply');
      setApplyMsg(r.data.message);
      if (r.data.applied > 0) load();
    } catch { } finally { setSaving(false); }
  };

  const openEditRecurring = (r) => {
    setRecurringForm({ categoria: r.categoria, monto: r.monto, dia_mes: r.dia_mes, activo: r.activo, branch_id: isAdminRole ? userBranchId : (r.sucursal_id ? String(r.sucursal_id) : '') });
    setRecurringModal(r);
  };

  const openNewRecurring = () => {
    setRecurringForm({ ...RECURRING_EMPTY, branch_id: userBranchId });
    setRecurringModal('new');
  };

  const openNewIncome = () => {
    setIncomeForm({ ...INCOME_EMPTY, branch_id: userBranchId });
    setIncomeModal(true);
  };

  const openNewExpense = () => {
    setExpenseForm({ ...EXPENSE_EMPTY, branch_id: userBranchId });
    setExpenseModal(true);
  };

  const hasBranches = branches.length > 0;

  const TABS = [
    { key: 'summary',   label: '📊 Resumen' },
    { key: 'income',    label: '📈 Ingresos' },
    { key: 'expenses',  label: '📉 Gastos' },
    { key: 'recurring', label: '🔁 Fijos' },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Finanzas</h2>
          <p>Control de ingresos, gastos y flujo de caja</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {tab === 'recurring'
            ? <button className="btn btn-primary" onClick={openNewRecurring}>+ Gasto Fijo</button>
            : <>
                <button className="btn btn-secondary" onClick={openNewExpense}>+ Gasto</button>
                <button className="btn btn-primary" onClick={openNewIncome}>+ Ingreso</button>
              </>}
        </div>
      </div>

      {/* Filtros */}
      {tab !== 'recurring' && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group">
              <label className="form-label">Desde</label>
              <input type="date" className="form-input" value={from} onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Hasta</label>
              <input type="date" className="form-input" value={to} onChange={e => setTo(e.target.value)} />
            </div>
            {hasBranches && (
              <div className="form-group">
                <label className="form-label">Sucursal</label>
                {isAdminRole
                  ? <input className="form-input" readOnly value={branches.find(b => String(b.id) === userBranchId)?.nombre || 'Mi sucursal'} style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-muted)' }} />
                  : <select className="form-select" value={filterBranch} onChange={e => setFilterBranch(e.target.value)}>
                      <option value="">Todas</option>
                      {branches.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                    </select>
                }
              </div>
            )}
            <button className="btn btn-primary" onClick={load}>🔍 Aplicar</button>
          </div>
        </div>
      )}

      {/* Filtro sucursal en tab Fijos */}
      {tab === 'recurring' && hasBranches && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Sucursal</label>
              {isAdminRole
                ? <input className="form-input" readOnly value={branches.find(b => String(b.id) === userBranchId)?.nombre || 'Mi sucursal'} style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-muted)' }} />
                : <select className="form-select" value={filterBranch} onChange={e => setFilterBranch(e.target.value)}>
                    <option value="">Todas</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                  </select>
              }
            </div>
          </div>
        </div>
      )}

      {applyMsg && (
        <div className="alert alert-success" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>✅ {applyMsg}</span>
          <button className="btn btn-ghost btn-icon" style={{ fontSize: 12 }} onClick={() => setApplyMsg('')}>✕</button>
        </div>
      )}

      {summary && tab !== 'recurring' && (
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          {[
            { label: 'Ingresos totales', value: fmtCurrency(summary.total_income),   icon: '📈', color: 'green' },
            { label: 'Gastos totales',   value: fmtCurrency(summary.total_expenses), icon: '📉', color: 'red'   },
            { label: 'Utilidad bruta',   value: fmtCurrency(summary.gross_profit),   icon: '💰', color: 'gold'  },
            { label: 'Margen',           value: `${summary.margin}%`,                icon: '📊', color: 'blue'  },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className={`stat-icon ${s.color}`}>{s.icon}</div>
              <div>
                <div className="stat-value" style={{ fontSize: '1.3rem' }}>{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--color-border)' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
              color: tab === t.key ? 'var(--color-gold)' : 'var(--color-text-muted)',
              borderBottom: tab === t.key ? '2px solid var(--color-gold)' : '2px solid transparent',
              fontFamily: 'var(--font-body)', fontSize: '0.875rem', fontWeight: tab === t.key ? 600 : 400, marginBottom: -1,
            }}>{t.label}</button>
        ))}
      </div>

      {/* ── Resumen ── */}
      {tab === 'summary' && summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: 16 }}>Ingresos por método de pago</h3>
            {summary.income_by_method.length === 0
              ? <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>Sin datos</p>
              : summary.income_by_method.map(m => (
                <div key={m.metodo_pago} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
                  <span>{m.metodo_pago}</span>
                  <span style={{ color: 'var(--color-gold)', fontWeight: 600 }}>{fmtCurrency(m.total)}</span>
                </div>
              ))}
          </div>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: 16 }}>Gastos por categoría</h3>
            {summary.expenses_by_category.length === 0
              ? <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>Sin datos</p>
              : summary.expenses_by_category.map(c => (
                <div key={c.categoria} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
                  <span>{c.categoria}</span>
                  <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>{fmtCurrency(c.total)}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ── Ingresos ── */}
      {tab === 'income' && (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Concepto</th>
                  <th>Método</th>
                  {hasBranches && <th>Sucursal</th>}
                  <th>Valor</th>
                  <th>Registrado por</th>
                </tr>
              </thead>
              <tbody>
                {income.length === 0
                  ? <tr><td colSpan={hasBranches ? 6 : 5} style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 24 }}>Sin registros en este período</td></tr>
                  : income.map(r => (
                    <tr key={r.id}>
                      <td>{fmtDate(r.fecha_ingreso)}</td>
                      <td>{r.concepto}</td>
                      <td>{r.metodo_pago}</td>
                      {hasBranches && <td style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>{r.sucursal_nombre || '—'}</td>}
                      <td style={{ color: 'var(--color-gold)', fontWeight: 600 }}>{fmtCurrency(r.monto)}</td>
                      <td style={{ color: 'var(--color-text-muted)' }}>{r.registered_by_name}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Gastos ── */}
      {tab === 'expenses' && (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Categoría</th>
                  <th>Observaciones</th>
                  {hasBranches && <th>Sucursal</th>}
                  <th>Fijo</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0
                  ? <tr><td colSpan={hasBranches ? 6 : 5} style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 24 }}>Sin registros en este período</td></tr>
                  : expenses.map(r => (
                    <tr key={r.id}>
                      <td>{fmtDate(r.fecha_gasto)}</td>
                      <td><span className="badge badge-gold">{r.categoria}</span></td>
                      <td>{r.subcategoria}</td>
                      {hasBranches && <td style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>{r.sucursal_nombre || '—'}</td>}
                      <td>{r.es_fijo ? '✅' : '—'}</td>
                      <td style={{ color: 'var(--color-error)', fontWeight: 600 }}>{fmtCurrency(r.monto)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Gastos Fijos ── */}
      {tab === 'recurring' && (
        <div>
          <div className="card" style={{ marginBottom: 16, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <strong>Gastos fijos del mes</strong>
              <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', margin: 0 }}>
                Se registran automáticamente cada mes en el día configurado.
              </p>
            </div>
            <button className="btn btn-secondary" onClick={applyNow} disabled={saving}>
              {saving ? <><div className="spinner" />Aplicando...</> : '⚡ Aplicar ahora'}
            </button>
          </div>

          <div className="card">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Categoría</th>
                    {hasBranches && <th>Sucursal</th>}
                    <th>Día del mes</th>
                    <th>Valor</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? <tr><td colSpan={hasBranches ? 6 : 5} style={{ textAlign: 'center', padding: 32 }}><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
                    : recurring.length === 0
                    ? <tr><td colSpan={hasBranches ? 6 : 5} style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 32 }}>
                        No hay gastos fijos configurados.<br />
                        <span style={{ fontSize: '0.82rem' }}>Usa "+ Gasto Fijo" para agregar arriendo, internet, servicios, etc.</span>
                      </td></tr>
                    : recurring.map(r => (
                      <tr key={r.id}>
                        <td><span className="badge badge-gold">{r.categoria}</span></td>
                        {hasBranches && <td style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>{r.sucursal_nombre || 'General'}</td>}
                        <td style={{ textAlign: 'center' }}>Día {r.dia_mes}</td>
                        <td style={{ color: 'var(--color-error)', fontWeight: 600 }}>{fmtCurrency(r.monto)}</td>
                        <td><span className={`badge badge-${r.activo ? 'active' : 'inactive'}`}>{r.activo ? 'Activo' : 'Inactivo'}</span></td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-sm btn-secondary" onClick={() => openEditRecurring(r)}>✏️</button>
                            <button className="btn btn-sm btn-danger" onClick={() => deleteRecurring(r.id)}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ingreso */}
      {incomeModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Registrar Ingreso</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setIncomeModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Concepto *</label>
                <input className="form-input" value={incomeForm.concept} onChange={e => setIncomeForm(f => ({ ...f, concept: e.target.value }))} />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Valor (COP) *</label>
                  <input type="number" className="form-input" value={incomeForm.amount} onChange={e => setIncomeForm(f => ({ ...f, amount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Método de pago</label>
                  <select className="form-select" value={incomeForm.payment_method} onChange={e => setIncomeForm(f => ({ ...f, payment_method: e.target.value }))}>
                    {PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha *</label>
                  <input type="date" className="form-input" value={incomeForm.income_date} onChange={e => setIncomeForm(f => ({ ...f, income_date: e.target.value }))} />
                </div>
                {hasBranches && (
                  <div className="form-group">
                    <label className="form-label">Sucursal</label>
                    {isAdminRole
                      ? <input className="form-input" readOnly value={branches.find(b => String(b.id) === userBranchId)?.nombre || 'Mi sucursal'} style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-muted)' }} />
                      : <select className="form-select" value={incomeForm.branch_id} onChange={e => setIncomeForm(f => ({ ...f, branch_id: e.target.value }))}>
                          <option value="">General</option>
                          {branches.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                        </select>
                    }
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setIncomeModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveIncome} disabled={saving || !incomeForm.concept || !incomeForm.amount}>
                {saving ? <><div className="spinner" />Guardando...</> : '💾 Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gasto */}
      {expenseModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Registrar Gasto</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setExpenseModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Categoría</label>
                  <select className="form-select" value={expenseForm.category} onChange={e => setExpenseForm(f => ({ ...f, category: e.target.value }))}>
                    {EXPENSE_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Observaciones</label>
                  <input className="form-input" value={expenseForm.subcategory} onChange={e => setExpenseForm(f => ({ ...f, subcategory: e.target.value }))} placeholder="Opcional" />
                </div>
                <div className="form-group">
                  <label className="form-label">Valor (COP) *</label>
                  <input type="number" className="form-input" value={expenseForm.amount} onChange={e => setExpenseForm(f => ({ ...f, amount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha *</label>
                  <input type="date" className="form-input" value={expenseForm.expense_date} onChange={e => setExpenseForm(f => ({ ...f, expense_date: e.target.value }))} />
                </div>
                {hasBranches && (
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Sucursal</label>
                    {isAdminRole
                      ? <input className="form-input" readOnly value={branches.find(b => String(b.id) === userBranchId)?.nombre || 'Mi sucursal'} style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-muted)' }} />
                      : <select className="form-select" value={expenseForm.branch_id} onChange={e => setExpenseForm(f => ({ ...f, branch_id: e.target.value }))}>
                          <option value="">General</option>
                          {branches.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                        </select>
                    }
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setExpenseModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveExpense} disabled={saving || !expenseForm.amount}>
                {saving ? <><div className="spinner" />Guardando...</> : '💾 Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gasto Fijo */}
      {recurringModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{recurringModal === 'new' ? 'Nuevo Gasto Fijo' : 'Editar Gasto Fijo'}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setRecurringModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Categoría</label>
                  <select className="form-select" value={recurringForm.categoria} onChange={e => setRecurringForm(f => ({ ...f, categoria: e.target.value }))}>
                    {EXPENSE_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Valor (COP) *</label>
                  <input type="number" className="form-input" value={recurringForm.monto} onChange={e => setRecurringForm(f => ({ ...f, monto: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Día del mes</label>
                  <input type="number" className="form-input" min={1} max={31} value={recurringForm.dia_mes} onChange={e => setRecurringForm(f => ({ ...f, dia_mes: Number(e.target.value) }))} />
                  <small style={{ color: 'var(--color-text-muted)', fontSize: '0.78rem' }}>Se registra automáticamente cada mes ese día.</small>
                </div>
                {hasBranches && (
                  <div className="form-group">
                    <label className="form-label">Sucursal</label>
                    {isAdminRole
                      ? <input className="form-input" readOnly value={branches.find(b => String(b.id) === userBranchId)?.nombre || 'Mi sucursal'} style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-muted)' }} />
                      : <select className="form-select" value={recurringForm.branch_id} onChange={e => setRecurringForm(f => ({ ...f, branch_id: e.target.value }))}>
                          <option value="">General (todas)</option>
                          {branches.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                        </select>
                    }
                  </div>
                )}
                {recurringModal !== 'new' && (
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <select className="form-select" value={recurringForm.activo ? '1' : '0'} onChange={e => setRecurringForm(f => ({ ...f, activo: e.target.value === '1' }))}>
                      <option value="1">Activo</option>
                      <option value="0">Inactivo</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setRecurringModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveRecurring} disabled={saving || !recurringForm.monto}>
                {saving ? <><div className="spinner" />Guardando...</> : '💾 Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
